import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import  PreviouslyOrderedColumn  from '../../components/dashboard/orders/order/PreviouslyOrderedColumn';
import { cloudinaryFull, cloudinaryThumbnail } from '../../utils/formatCloudinary';
import { fCurrency } from '../../utils/formatNumber';
import Image from '../image';
import Lightbox from '../lightbox';
import UOMDropdownForCatalog from './UOMDropdownForCatalog';

const columnContentStyle = {
  wordWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word'
}

export const renderColorBox = (value, color) => (
  <Typography
    color={color || 'primary'}
    variant="subtitle2"
    sx={{
      border: 1,
      minWidth: '20px',
      minHeight: '20px',
      borderRadius: '3px',
    }}
  >
    <Box
      sx={{
        flexGrow: 1,
        overflow: 'hidden', // Hide overflow
        textOverflow: 'ellipsis', // Add ellipsis for overflow
        whiteSpace: 'nowrap', // Prevent wrapping
        m: 0.5,
      }}
    >
      {value || ''}
    </Box>
  </Typography>
);
export default function RenderImage({ pictures }) {
  RenderImage.propTypes = {
    pictures: PropTypes.array,
  };

  const picture = pictures[0];
  const [selectedImage, setSelectedImage] = useState(-1);
  const handleOpenLightbox = () => {
    setSelectedImage(0);
  };
  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };

  return (
    <>
      <Image
        alt="attachment"
        src={
          pictures?.length
            ? cloudinaryThumbnail(picture)
            : cloudinaryThumbnail(
                'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
              )
        }
        sx={{
          cursor: 'pointer',
          maxWidth: '50px',
          '&:hover': {
            opacity: 0.9,
          },
        }}
        onClick={() => handleOpenLightbox()}
      />
      <Lightbox
        index={0}
        slides={pictures?.map((picture) => ({ src: cloudinaryFull(picture) }))}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
}

const picture = {
  field: 'picture',
  headerName: 'Image',
  flex: 0.4,
  renderCell: (params) => <RenderImage pictures={params.row.productDetails[0].pictures} />,
  sortable: false,
  filterable: false,
};

const description = {
  field: 'description',
  headerName: 'Description',
  width: 300,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{ ...columnContentStyle, fontSize: '0.7rem' }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};

const subcategory = {
  field: 'displaySubcategory',
  headerName: 'Subcategory',
  width: 75,
  renderCell: (params) => (
    <Box
      component="div"
      sx={columnContentStyle}
    >
      {params.row.displaySubcategory.name}
    </Box>
  ),
  valueGetter: (params) => params.row.displaySubcategory.name,
  type: 'customSelect',
  path: 'displaySubcategory.name',
  customType: 'catalogCategory'
};
const category = {
  field: 'displayCategory',
  headerName: 'Category',
  flex: 1,
  renderCell: (params) => renderColorBox(params.row.displayCategory.name, 'secondary'),
  valueGetter: (params) => params.row.displayCategory.name,
  type: 'customSelect',
  path: 'displayCategory.name',
  customType: 'catalogCategory'
};
const price = {
  field: 'price',
  headerName: 'Price',
  flex: 0.5,
  renderCell: (params) => params.row.invalidItem ? "" : fCurrency(params.row.productDetails[0].price),
  valueGetter: (params) => params.row.productDetails[0].price,
  type: 'number',
  headerAlign: 'left',
  align: 'left',
};
const id = {
  field: 'id',
  headerName: 'PS ID',
  flex: 0.5,
  renderCell: (params) => params.row.invalidItem ? "Item Unavailable" : <Box sx={columnContentStyle}>
    {`PS${params.row.id}`}
  </Box>,
  valueGetter: (params) => `PS${params.row.id}`,
  getFilterValues: (row) => [row.invalidItem ? "Item Unavailable" : `PS${row.id}`],
  type: 'customSelect',
  headerAlign: 'left',
  align: 'left',
};
const uom = {
  field: 'uom',
  headerName: 'UOM',
  flex: 0.5,
  type: 'number',
  headerAlign: 'left',
  align: 'left',
  // valueGetter: (params) => params.row.productDetails[0].uomAmount,
  renderCell: (params) => {
    const { uomAmount, uomType } = params.row.productDetails[0];
    return <Box sx={columnContentStyle}>{`${uomAmount}/${uomType}`}</Box>
  },
  valueGetter: (params) => {
    const { uomAmount, uomType } = params.row.productDetails[0];
    return `${uomAmount}/${uomType}`;
  },
};

const changeUom = {
  field: 'change_uom',
  headerName: 'UOM',
  renderCell: (params) => {
    const { uomAmount, uomType } = params.row.productDetails[0];

    return params.row.productDetails?.[0]?.productDetailUOMs?.length > 0 ? (
      <UOMDropdownForCatalog row={params.row} />
    ) : (
      <Box sx={columnContentStyle}>{`${uomAmount}/${uomType}`}</Box>
    );
  },
  valueGetter: (params) => {
    const { uomAmount, uomType } = params.row.productDetails[0];
    return `${uomAmount}/${uomType}`;
  },
  getFilterValues: (row) => {
    const { uomAmount, uomType } = row.productDetails[0];
    return [`${uomAmount}/${uomType}`];
  },
  width: 90,
  type: 'customSelect',
};

const manufacturer = {
  field: 'manufacturer',
  headerName: 'Manufacturer',
  flex: 1.3,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {params.row.productDetails[0].manufacturer}
    </Box>
  ),
  type: 'customSelect',
  getFilterValues: (row) => [row.productDetails[0].manufacturer].filter((v) => v),
  valueGetter: (params) => params.row.productDetails[0].manufacturer,
};
const manufacturerId = {
  field: 'manufacturerId',
  headerName: ' Id',
  flex: 0.8,
  renderCell: (params) => (
    <Box sx={columnContentStyle}>
      {params.row.productDetails[0].manufacturerId}
    </Box>
  ),
  type: 'customSelect',
  valueGetter: (params) => params.row.productDetails[0].manufacturerId,
  getFilterValues: (row) => [row.productDetails[0].manufacturerId].filter((v) => v),
};
const vendorItemId = {
  field: 'vendorItemId',
  headerName: 'SKU',
  flex: 0.8,
  renderCell: (params) => {
    const hasLink = params.row.productDetails[0]?.vendorItemLink;
    return hasLink ? (
      <Box
        component="a"
        sx={columnContentStyle}
        href={params.row.productDetails[0]?.vendorItemLink}
        target="_blank"
        rel="noreferrer"
      >
        {params.row.productDetails[0]?.vendorItemId}
      </Box>
    ) : (
      <Box sx={columnContentStyle}>
        {params.row.productDetails[0].vendorItemId}
      </Box>
    );
  },
  type: 'customSelect',
  valueGetter: (params) => params.row.productDetails[0]?.vendorItemId,
  getFilterValues: (row) => [row.productDetails[0].vendorItemId].filter((v) => v),
};

const baseUom = {
  field: 'baseUom',
  headerName: 'Base UOM',
  flex: 0.5,
  type: 'customSelect',
  headerAlign: 'left',
  align: 'left',
  renderCell: (params) => {
    <Box sx={columnContentStyle}>
      {
        `${params.row.productDetails[0].baseUOMAmount ? params.row.productDetails[0].baseUOMAmount : ''}${params.row.productDetails[0].baseUOMType ? '/' : ''
        }${params.row.productDetails[0].baseUOMType ? params.row.productDetails[0].baseUOMType : ''}`
      }
    </Box>
  },
  valueGetter: (params) => {
    const details = params.row.productDetails?.[0] || {};
    return `${details.baseUOMAmount ?? ''}${details.baseUOMType ? `/${details.baseUOMType}` : ''}`;
  },
  getFilterValues: (row) => {
    const details = row.productDetails?.[0] || {};
    return [`${details.baseUOMAmount ?? ''}${details.baseUOMType ? `/${details.baseUOMType}` : ''}`].filter(v => v);
  }
};

const previouslyOrderedItems = {
  field: 'previouslyOrderedItems',
  headerName: 'History',
  width: 50,
  renderCell: (params) => <PreviouslyOrderedColumn previouslyOrderedItems={params.row.productDetails[0].previouslyOrderedItems || []} />,
  valueGetter: (params) => params.row.productDetails[0].previouslyOrderedItems?.map((item) => item.ordered).join(', '),
};

const SHOP_CATALOG = [
  picture,
  description,
  vendorItemId,
  subcategory,
  price,
  id,
  manufacturer,
  manufacturerId,
  changeUom,
  baseUom,
  previouslyOrderedItems,
];
const CATALOG = [
  picture,
  description,
  vendorItemId,
  category,
  subcategory,
  price,
  id,
  changeUom,
  baseUom,
  previouslyOrderedItems,
];

export { CATALOG, SHOP_CATALOG };
